import { useEffect, useMemo, useState } from 'react';
import {
  ESTRATEGIAS_PREDICTOR,
  TIPOS_ORGANISMO,
  formatearCLP,
  formatearPct,
  type CasoValidacionHistorica,
  type ModoEstrategiaPredictor,
  type ParametrosPredictor,
  type SimulacionPlaygroundRespuesta,
  type TestImpactoRespuesta,
  type TipoOrganismo,
} from '@lexicore/shared';
import {
  IconoAlerta,
  IconoCerebro,
  IconoCerrar,
  IconoCheck,
  IconoEscudoCheck,
  IconoMotorIA,
  IconoRayo,
  IconoRestablecer,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { useAuth } from '../contextos/AuthContext';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useBrechasDePlazo,
  useCategoriasComercialesConfig,
  useConsolaMotor,
  useDiagnosticoPredictor,
  useValidacionHistorica,
} from '../lib/consultas';
import {
  useActualizarParametros,
  useAplicarEstrategiaMotor,
  useRestablecerParametros,
  useSimularPlayground,
  useTestImpacto,
} from '../lib/mutaciones';

/**
 * Controles de calibración manual con explicaciones claras en lenguaje de negocios.
 */
const CONTROLES: {
  clave: keyof ParametrosPredictor;
  etiqueta: string;
  explicacion: string;
  min: number;
  max: number;
  paso: number;
  sufijo?: string;
  grupo: 'Ponderación de Datos' | 'Estrategia Comercial' | 'Robustez';
}[] = [
  {
    clave: 'vidaMediaMeses',
    etiqueta: 'Vida media de la recencia',
    explicacion:
      'Cada cuántos meses una licitación pasada pierde el 50% de su peso probabilístico. Con 8 meses, lo de hace un año pesa mucho menos que lo de hoy.',
    min: 1,
    max: 36,
    paso: 1,
    sufijo: 'meses',
    grupo: 'Ponderación de Datos',
  },
  {
    clave: 'pesoMismoOrganismo',
    etiqueta: 'Peso del mismo organismo',
    explicacion:
      'Multiplicador de valor si la licitación es del mismo comprador público. Cada institución tiene su propia cultura de precios.',
    min: 1,
    max: 10,
    paso: 0.1,
    sufijo: '×',
    grupo: 'Ponderación de Datos',
  },
  {
    clave: 'pesoMismoTipoOrganismo',
    etiqueta: 'Peso del mismo tipo de organismo',
    explicacion: 'Cuánto pondera un comprador del mismo sector (ej. dos municipalidades o dos hospitales).',
    min: 1,
    max: 10,
    paso: 0.1,
    sufijo: '×',
    grupo: 'Ponderación de Datos',
  },
  {
    clave: 'pesoMismoTipoProceso',
    etiqueta: 'Peso del tipo de proceso',
    explicacion: 'Una compra ágil se comporta de forma muy distinta a una licitación pública (LP o LE).',
    min: 1,
    max: 10,
    paso: 0.1,
    sufijo: '×',
    grupo: 'Ponderación de Datos',
  },
  {
    clave: 'agresividadPrecio',
    etiqueta: 'Agresividad de precio',
    explicacion:
      'Factor multiplicador sobre la media estimada de mercado. 0,95 significa ofertar con un 5% de descuento para ganar volumen.',
    min: 0.5,
    max: 1.3,
    paso: 0.01,
    grupo: 'Estrategia Comercial',
  },
  {
    clave: 'agresividadPlazo',
    etiqueta: 'Agresividad de plazo',
    explicacion:
      'Factor de ajuste de tiempo de entrega. 0,95 significa prometer un 5% más rápido para ganar puntos en la pauta técnica.',
    min: 0.5,
    max: 1.3,
    paso: 0.01,
    grupo: 'Estrategia Comercial',
  },
  {
    clave: 'margenMinimoPct',
    etiqueta: 'Piso mínimo de margen (%)',
    explicacion:
      'Guardarraíl de seguridad: el motor jamás sugerirá un precio que deje a la empresa por debajo de este margen de rentabilidad.',
    min: 0,
    max: 80,
    paso: 1,
    sufijo: '%',
    grupo: 'Estrategia Comercial',
  },
  {
    clave: 'plazoMinimoDias',
    etiqueta: 'Plazo mínimo de entrega',
    explicacion: 'Límite físico inferior en días hábiles; el motor nunca sugerirá menos de este plazo.',
    min: 1,
    max: 90,
    paso: 1,
    sufijo: 'días',
    grupo: 'Estrategia Comercial',
  },
  {
    clave: 'winsorPct',
    etiqueta: 'Filtro de ofertas absurdas (Winsorización)',
    explicacion:
      'Porcentaje de ofertas extremadamente altas o bajas que se recortan antes de promediar para evitar distorsiones.',
    min: 0,
    max: 25,
    paso: 1,
    sufijo: '%',
    grupo: 'Robustez',
  },
];

const GRUPOS_CONTROLES = ['Ponderación de Datos', 'Estrategia Comercial', 'Robustez'] as const;

export function Motor() {
  const avisos = useAvisos();
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'ADMIN' || usuario?.rol === 'OWNER';

  // Consultas principales
  const consolaQuery = useConsolaMotor();
  const categoriasQuery = useCategoriasComercialesConfig(false);
  const diagnosticoQuery = useDiagnosticoPredictor();
  const brechasQuery = useBrechasDePlazo();

  // Mutaciones
  const aplicarEstrategiaMut = useAplicarEstrategiaMotor();
  const simularPlaygroundMut = useSimularPlayground();
  const testImpactoMut = useTestImpacto();
  const actualizarParametrosMut = useActualizarParametros();
  const restablecerParametrosMut = useRestablecerParametros();

  // Estado para filtros de Validación Histórica
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroCategoriaId, setFiltroCategoriaId] = useState('');
  const [filtroResultado, setFiltroResultado] = useState<'TODOS' | 'HABRIAMOS_GANADO' | 'SOBREPRECIO' | 'SUBCOTIZADO'>('TODOS');
  const [minimoObs, setMinimoObs] = useState<number>(1);

  // Consulta de Validación Histórica conectada a los filtros
  const validacionQuery = useValidacionHistorica({
    categoriaId: filtroCategoriaId || undefined,
    minimoObservaciones: minimoObs,
  });

  // Estado para el Simulador / Playground en Vivo
  const [simPresupuesto, setSimPresupuesto] = useState<number>(30000000);
  const [simCategoriaId, setSimCategoriaId] = useState<string>('');
  const [simTipoOrganismo, setSimTipoOrganismo] = useState<TipoOrganismo>('MUNICIPALIDAD');
  const [simOferentes, setSimOferentes] = useState<number>(4);
  const [simCostoDirecto, setSimCostoDirecto] = useState<number>(21000000);
  const [simMargenMinimo, setSimMargenMinimo] = useState<number>(18);
  const [simResultado, setSimResultado] = useState<SimulacionPlaygroundRespuesta | null>(null);

  // Estado para Calibración Manual de Parámetros
  const [borradorParametros, setBorradorParametros] = useState<ParametrosPredictor | null>(null);

  // Estado para el Modal de Test de Impacto (Antes vs Después)
  const [modalImpactoAbierto, setModalImpactoAbierto] = useState(false);
  const [modoParaImpacto, setModoParaImpacto] = useState<ModoEstrategiaPredictor>('AGRESIVO');
  const [resultadoImpacto, setResultadoImpacto] = useState<TestImpactoRespuesta | null>(null);

  // Parámetros activos vigentes
  const parametrosActuales = consolaQuery.data?.parametrosActuales;
  const valoresParametros = borradorParametros ?? parametrosActuales ?? null;

  // Detección de cambios manuales
  const hayCambiosManuales = useMemo(() => {
    if (!borradorParametros || !parametrosActuales) return false;
    return CONTROLES.some((c) => borradorParametros[c.clave] !== parametrosActuales[c.clave]);
  }, [borradorParametros, parametrosActuales]);

  // Selección por defecto de categoría para el simulador
  useEffect(() => {
    if (categoriasQuery.data && categoriasQuery.data.length > 0 && !simCategoriaId) {
      const primera = categoriasQuery.data[0];
      if (primera) setSimCategoriaId(primera.id);
    }
  }, [categoriasQuery.data, simCategoriaId]);

  // Efecto reactivo del Simulador / Playground (Debounce de 250ms)
  useEffect(() => {
    if (simPresupuesto <= 0) return;

    const timer = setTimeout(async () => {
      try {
        const res = await simularPlaygroundMut.mutateAsync({
          presupuestoOficialNeto: simPresupuesto,
          categoriaId: simCategoriaId || undefined,
          tipoOrganismo: simTipoOrganismo,
          cantidadOferentes: simOferentes,
          costoDirectoNeto: simCostoDirecto > 0 ? simCostoDirecto : undefined,
          margenMinimoPct: simMargenMinimo,
        });
        setSimResultado(res);
      } catch {
        // En caso de falla temporal en simulación, se mantiene el estado previo
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [simPresupuesto, simCategoriaId, simTipoOrganismo, simOferentes, simCostoDirecto, simMargenMinimo]);

  if (consolaQuery.isLoading) return <Cargando texto="Cargando consola del motor predictivo…" />;
  if (consolaQuery.isError) {
    return <ErrorCarga error={consolaQuery.error} reintentar={() => void consolaQuery.refetch()} />;
  }
  if (!consolaQuery.data || !valoresParametros) return null;

  const { kpis, diagnostico } = consolaQuery.data;

  // Acciones
  const handleAplicarEstrategia = async (modo: ModoEstrategiaPredictor) => {
    try {
      await aplicarEstrategiaMut.mutateAsync({ modo });
      setBorradorParametros(null);
      avisos.exito(`Estrategia ${ESTRATEGIAS_PREDICTOR[modo].nombre} activada exitosamente.`);
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo aplicar la estrategia.');
    }
  };

  const handleGuardarParametrosManuales = async () => {
    if (!borradorParametros) return;
    try {
      await actualizarParametrosMut.mutateAsync(borradorParametros);
      setBorradorParametros(null);
      avisos.exito('Parámetros de calibración guardados. Las nuevas ofertas ya los aplican.');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al guardar los parámetros.');
    }
  };

  const handleRestablecerFabrica = async () => {
    if (!window.confirm('¿Deseas restablecer todos los parámetros del motor a los valores óptimos de fábrica?')) return;
    try {
      await restablecerParametrosMut.mutateAsync();
      setBorradorParametros(null);
      avisos.exito('Parámetros restablecidos a la configuración de fábrica recomendada.');
    } catch (err) {
      avisos.error('No se pudo restablecer la configuración.');
    }
  };

  const handleAbrirTestImpacto = async (modo: ModoEstrategiaPredictor = 'AGRESIVO') => {
    setModoParaImpacto(modo);
    setModalImpactoAbierto(true);
    setResultadoImpacto(null);
    try {
      const res = await testImpactoMut.mutateAsync({ modo });
      setResultadoImpacto(res);
    } catch (err) {
      avisos.error('No se pudo calcular la proyección de impacto.');
    }
  };

  const handleAplicarDesdeModalImpacto = async () => {
    if (!modoParaImpacto) return;
    await handleAplicarEstrategia(modoParaImpacto);
    setModalImpactoAbierto(false);
  };

  // Filtrado de la Validación Histórica
  const casosHistoricos = validacionQuery.data?.casos ?? [];
  const resumenHistorico = validacionQuery.data?.resumen;

  const casosFiltrados = casosHistoricos.filter((caso: CasoValidacionHistorica) => {
    if (filtroResultado !== 'TODOS' && caso.resultado !== filtroResultado) return false;
    if (filtroBusqueda.trim()) {
      const q = filtroBusqueda.toLowerCase().trim();
      const coincideCodigo = (caso.codigoExterno ?? '').toLowerCase().includes(q);
      const coincideTitulo = caso.titulo.toLowerCase().includes(q);
      const coincideOrganismo = caso.organismoNombre.toLowerCase().includes(q);
      if (!coincideCodigo && !coincideTitulo && !coincideOrganismo) return false;
    }
    return true;
  });

  const hayFiltrosHistoricosActivos =
    filtroBusqueda.trim() !== '' || filtroCategoriaId !== '' || filtroResultado !== 'TODOS' || minimoObs !== 1;

  const limpiarFiltrosHistoricos = () => {
    setFiltroBusqueda('');
    setFiltroCategoriaId('');
    setFiltroResultado('TODOS');
    setMinimoObs(1);
  };

  return (
    <>
      {/* ══ Encabezado Principal ══ */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', margin: 0 }}>
              Motor Predictivo de Precio & Plazos
            </h1>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 'var(--radius-full)',
                fontSize: 11,
                fontWeight: 700,
                background:
                  diagnostico.estrategiaActiva === 'AGRESIVO'
                    ? 'var(--accent-yellow-soft)'
                    : diagnostico.estrategiaActiva === 'EQUILIBRADO'
                    ? 'var(--accent-green-soft)'
                    : 'var(--accent-blue-soft)',
                color:
                  diagnostico.estrategiaActiva === 'AGRESIVO'
                    ? 'var(--accent-yellow)'
                    : diagnostico.estrategiaActiva === 'EQUILIBRADO'
                    ? 'var(--accent-green)'
                    : 'var(--accent-blue)',
                border: '1px solid currentColor',
              }}
            >
              <IconoCerebro size={13} />
              MODO {diagnostico.estrategiaActiva} ACTIVO
            </span>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, maxWidth: 840, lineHeight: 1.55 }}>
            Inteligencia comercial con cero caja negra. Combina la recencia de compras públicas, afinidad por organismo y
            costos directos para calcular ofertas ganadoras con un guardarraíl inquebrantable de rentabilidad.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handleAbrirTestImpacto(diagnostico.estrategiaActiva === 'AGRESIVO' ? 'EQUILIBRADO' : 'AGRESIVO')}
          >
            <IconoMotorIA size={14} />
            <span>Test de Impacto</span>
          </button>
          {esAdmin && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleRestablecerFabrica}
              disabled={restablecerParametrosMut.isPending}
            >
              <IconoRestablecer size={13} />
              <span>Restablecer de Fábrica</span>
            </button>
          )}
        </div>
      </div>

      {/* ══ Top 4 KPIs Ejecutivos ══ */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <TarjetaKpi
          etiqueta="Precisión Global del Motor"
          valor={`${kpis.precisionGlobalPct}%`}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota="acierto en backtesting histórico"
        />
        <TarjetaKpi
          etiqueta="Error Medio (MAPE)"
          valor={`±${kpis.errorMedioPct}%`}
          acento={kpis.errorMedioPct <= 10 ? 'var(--accent-green)' : 'var(--accent-yellow)'}
          colorValor={kpis.errorMedioPct <= 10 ? 'var(--accent-green)' : 'var(--accent-yellow)'}
          nota="desviación frente al adjudicatario real"
        />
        <TarjetaKpi
          etiqueta="Licitaciones Útiles Evaluadas"
          valor={kpis.licitacionesUtiles}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota="procesos con monto de adjudicación"
        />
        <TarjetaKpi
          etiqueta="Sesgo de Cotización"
          valor={formatearPct(kpis.sesgoCotizacionPct, 1)}
          acento="var(--accent-purple)"
          colorValor="var(--accent-purple)"
          nota={
            kpis.sesgoCotizacionPct > 0
              ? 'tiende a cotizar sobre el mercado'
              : kpis.sesgoCotizacionPct < 0
              ? 'tiende a cotizar bajo el mercado'
              : 'perfectamente centrado y neutral'
          }
        />
      </div>

      {/* ══ Diagnóstico & Salud de Datos ══ */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '14px 18px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              padding: '4px 8px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 11,
              fontWeight: 800,
              background:
                diagnostico.saludDatos === 'ALTA'
                  ? 'var(--accent-green-soft)'
                  : diagnostico.saludDatos === 'MEDIA'
                  ? 'var(--accent-yellow-soft)'
                  : 'var(--bg-hover)',
              color:
                diagnostico.saludDatos === 'ALTA'
                  ? 'var(--accent-green)'
                  : diagnostico.saludDatos === 'MEDIA'
                  ? 'var(--accent-yellow)'
                  : 'var(--text-muted)',
              border: '1px solid currentColor',
            }}
          >
            SALUD {diagnostico.saludDatos}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {diagnostico.mensajeDiagnostico}
          </span>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'right' }}>
          <strong>{diagnostico.licitacionesConPrecioGanador}</strong> de {diagnostico.totalLicitacionesCerradas} licitaciones
          con precio de adjudicación registrado
        </div>
      </div>

      {/* ══ 1. Presets de Postura Comercial (1 Clic) ══ */}
      <Panel
        titulo="1. Postura Comercial Predefinida (Selecciona con 1 Clic)"
        subtitulo="Cambia instantáneamente la estrategia de toda la empresa según la meta del trimestre: capturar mercado o asegurar margen alto."
        style={{ marginBottom: 24 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {/* Preset Agresivo */}
          <div
            style={{
              background: diagnostico.estrategiaActiva === 'AGRESIVO' ? 'var(--accent-yellow-soft)' : 'var(--bg-card)',
              border: `2px solid ${diagnostico.estrategiaActiva === 'AGRESIVO' ? 'var(--accent-yellow)' : 'var(--border-subtle)'}`,
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: diagnostico.estrategiaActiva === 'AGRESIVO' ? '0 0 0 1px var(--accent-yellow)' : 'var(--shadow-card)',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconoRayo size={16} style={{ color: 'var(--accent-yellow)' }} />
                  MODO AGRESIVO
                </span>
                {diagnostico.estrategiaActiva === 'AGRESIVO' && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--accent-yellow)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 4 }}>
                    ACTIVO
                  </span>
                )}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
                Foco: Captura de mercado y penetración. Sugiere precios con descuento para ganar licitaciones prioritarias.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Oferta sugerida:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>~83% del presupuesto</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Probabilidad estimada:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>~85% de ganar</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Piso de margen mínimo:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>15%</strong>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`btn btn-sm ${diagnostico.estrategiaActiva === 'AGRESIVO' ? 'btn-secondary' : 'btn-primary'}`}
                style={{ flex: 1 }}
                onClick={() => handleAplicarEstrategia('AGRESIVO')}
                disabled={diagnostico.estrategiaActiva === 'AGRESIVO' || aplicarEstrategiaMut.isPending || !esAdmin}
              >
                {diagnostico.estrategiaActiva === 'AGRESIVO' ? '✓ Modo Agresivo Activo' : 'Aplicar Modo Agresivo'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                title="Proyectar antes de aplicar"
                onClick={() => handleAbrirTestImpacto('AGRESIVO')}
              >
                Proyectar
              </button>
            </div>
          </div>

          {/* Preset Equilibrado */}
          <div
            style={{
              background: diagnostico.estrategiaActiva === 'EQUILIBRADO' ? 'var(--accent-green-soft)' : 'var(--bg-card)',
              border: `2px solid ${diagnostico.estrategiaActiva === 'EQUILIBRADO' ? 'var(--accent-green)' : 'var(--border-subtle)'}`,
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: diagnostico.estrategiaActiva === 'EQUILIBRADO' ? '0 0 0 1px var(--accent-green)' : 'var(--shadow-card)',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconoCerebro size={16} style={{ color: 'var(--accent-green)' }} />
                  MODO EQUILIBRADO
                </span>
                {diagnostico.estrategiaActiva === 'EQUILIBRADO' && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--accent-green)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 4 }}>
                    RECOMENDADO
                  </span>
                )}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
                Foco: Balance óptimo entre probabilidad matemática de victoria y rentabilidad comercial neta.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Oferta sugerida:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>~88% del presupuesto</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Probabilidad estimada:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-green)' }}>~72% de ganar</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Piso de margen mínimo:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>18%</strong>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`btn btn-sm ${diagnostico.estrategiaActiva === 'EQUILIBRADO' ? 'btn-secondary' : 'btn-primary'}`}
                style={{ flex: 1 }}
                onClick={() => handleAplicarEstrategia('EQUILIBRADO')}
                disabled={diagnostico.estrategiaActiva === 'EQUILIBRADO' || aplicarEstrategiaMut.isPending || !esAdmin}
              >
                {diagnostico.estrategiaActiva === 'EQUILIBRADO' ? '✓ Modo Equilibrado Activo' : 'Aplicar Equilibrado'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                title="Proyectar antes de aplicar"
                onClick={() => handleAbrirTestImpacto('EQUILIBRADO')}
              >
                Proyectar
              </button>
            </div>
          </div>

          {/* Preset Conservador / Rentabilidad */}
          <div
            style={{
              background: diagnostico.estrategiaActiva === 'CONSERVADOR' ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
              border: `2px solid ${diagnostico.estrategiaActiva === 'CONSERVADOR' ? 'var(--accent-blue)' : 'var(--border-subtle)'}`,
              borderRadius: 'var(--radius-md)',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: diagnostico.estrategiaActiva === 'CONSERVADOR' ? '0 0 0 1px var(--accent-blue)' : 'var(--shadow-card)',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconoEscudoCheck size={16} style={{ color: 'var(--accent-blue)' }} />
                  MODO RENTABILIDAD / COSECHA
                </span>
                {diagnostico.estrategiaActiva === 'CONSERVADOR' && (
                  <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--accent-blue)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 4 }}>
                    ACTIVO
                  </span>
                )}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
                Foco: Margen alto y seguro. Ideal cuando el equipo de ingenieros está con capacidad casi llena.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Oferta sugerida:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>~94% del presupuesto</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Probabilidad estimada:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)' }}>~45% de ganar</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Piso de margen mínimo:</span>
                  <strong style={{ fontFamily: 'var(--font-mono)' }}>22%</strong>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`btn btn-sm ${diagnostico.estrategiaActiva === 'CONSERVADOR' ? 'btn-secondary' : 'btn-primary'}`}
                style={{ flex: 1 }}
                onClick={() => handleAplicarEstrategia('CONSERVADOR')}
                disabled={diagnostico.estrategiaActiva === 'CONSERVADOR' || aplicarEstrategiaMut.isPending || !esAdmin}
              >
                {diagnostico.estrategiaActiva === 'CONSERVADOR' ? '✓ Modo Rentabilidad Activo' : 'Aplicar Rentabilidad'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                title="Proyectar antes de aplicar"
                onClick={() => handleAbrirTestImpacto('CONSERVADOR')}
              >
                Proyectar
              </button>
            </div>
          </div>
        </div>
      </Panel>

      {/* ══ 2. Simulador Interactivo / Playground en Vivo ══ */}
      <Panel
        titulo="2. Simulador en Vivo (Playground con Guardarraíl de Margen)"
        subtitulo="Simula una licitación en tiempo real. Modifica los parámetros y verifica al instante si el precio sugerido protege tu rentabilidad o dispara la alerta de seguridad."
        style={{ marginBottom: 24 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
          {/* Controles del Simulador */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label htmlFor="sim_presupuesto">Presupuesto Oficial Neto ($ CLP)</label>
              <input
                id="sim_presupuesto"
                type="number"
                min={100000}
                step={500000}
                value={simPresupuesto}
                onChange={(e) => setSimPresupuesto(Number(e.target.value))}
                style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {formatearCLP(simPresupuesto)} CLP
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="sim_categoria">Línea de Negocio</label>
                <select
                  id="sim_categoria"
                  value={simCategoriaId}
                  onChange={(e) => setSimCategoriaId(e.target.value)}
                >
                  <option value="">Cualquier Categoría</option>
                  {(categoriasQuery.data ?? []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="sim_sector">Sector Comprador</label>
                <select
                  id="sim_sector"
                  value={simTipoOrganismo}
                  onChange={(e) => setSimTipoOrganismo(e.target.value as TipoOrganismo)}
                >
                  {TIPOS_ORGANISMO.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="sim_costo">Costo Directo Estimado ($ CLP)</label>
                <input
                  id="sim_costo"
                  type="number"
                  min={0}
                  step={500000}
                  value={simCostoDirecto}
                  onChange={(e) => setSimCostoDirecto(Number(e.target.value))}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {formatearCLP(simCostoDirecto)} CLP
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="sim_oferentes">Competidores Estimados</label>
                <input
                  id="sim_oferentes"
                  type="number"
                  min={1}
                  max={20}
                  value={simOferentes}
                  onChange={(e) => setSimOferentes(Number(e.target.value))}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {simOferentes} empresas ofertando
                </span>
              </div>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <label htmlFor="sim_margen">Piso de Margen Exigido (%)</label>
                <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-blue)' }}>{simMargenMinimo}%</strong>
              </div>
              <input
                id="sim_margen"
                type="range"
                min={5}
                max={50}
                step={1}
                value={simMargenMinimo}
                onChange={(e) => setSimMargenMinimo(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--accent-blue)' }}
              />
            </div>
          </div>

          {/* Resultado de la Simulación en Vivo */}
          <div
            style={{
              background: 'var(--bg-hover)',
              borderRadius: 'var(--radius-lg)',
              padding: '20px',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {simResultado ? (
              <>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                    Recomendación Óptima Calculada
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 26, fontWeight: 900, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      {formatearCLP(simResultado.precioRecomendadoNeto)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      Descuento del <strong>{simResultado.descuentoSobrePresupuestoPct}%</strong> sobre el presupuesto oficial
                    </div>
                  </div>

                  {/* Alerta de Seguridad Inquebrantable */}
                  {simResultado.alertaPisoMargen && (
                    <div
                      style={{
                        padding: '12px 14px',
                        background: 'var(--accent-red-soft)',
                        border: '1px solid var(--accent-red)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--accent-red)',
                        fontSize: 12,
                        lineHeight: 1.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        marginBottom: 14,
                      }}
                    >
                      <IconoAlerta size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <strong>GUARDARRAÍL DE SEGURIDAD ACTIVADO:</strong>
                        <div style={{ marginTop: 2 }}>
                          El precio sugerido genera un margen del {simResultado.margenEstimadoPct?.toFixed(1)}%, perforando tu piso
                          mínimo del {simMargenMinimo}%. Riesgo de ganar perdiendo dinero.
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: 'var(--bg-card)', padding: 12, borderRadius: 'var(--radius-sm)' }}>
                      <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Probabilidad de Ganar</span>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          fontFamily: 'var(--font-mono)',
                          color:
                            simResultado.probabilidadGanarPct >= 70
                              ? 'var(--accent-green)'
                              : simResultado.probabilidadGanarPct >= 40
                              ? 'var(--accent-yellow)'
                              : 'var(--accent-red)',
                        }}
                      >
                        {simResultado.probabilidadGanarPct.toFixed(1)}%
                      </div>
                    </div>

                    <div style={{ background: 'var(--bg-card)', padding: 12, borderRadius: 'var(--radius-sm)' }}>
                      <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Margen Neto Proyectado</span>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          fontFamily: 'var(--font-mono)',
                          color: simResultado.alertaPisoMargen ? 'var(--accent-red)' : 'var(--accent-green)',
                        }}
                      >
                        {simResultado.margenEstimadoPct != null ? `${simResultado.margenEstimadoPct.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 11.5,
                    color: 'var(--text-muted)',
                    background: 'var(--bg-card)',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-sm)',
                    lineHeight: 1.5,
                  }}
                >
                  💡 <strong>Diagnóstico:</strong> {simResultado.mensajeEstrategia}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Calculando escenario en vivo…
              </div>
            )}
          </div>
        </div>
      </Panel>

      {/* ══ 3. Tabla de Validación Histórica ("Habríamos Ganado") con Filtros 100% ══ */}
      <Panel
        titulo="3. Validación Histórica: «¿Habríamos Ganado?» (Backtesting Sin Ver el Futuro)"
        subtitulo="Auditoría matemática temporal contra todas las licitaciones resueltas de la empresa. Evalúa qué habría sugerido el motor usando únicamente los datos previos a cada fecha."
        style={{ marginBottom: 24 }}
      >
        {/* Resumen Superior del Backtesting */}
        {resumenHistorico && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 18 }}>
            <div style={{ background: 'var(--bg-hover)', padding: '12px 14px', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>🏆 Habríamos Ganado</span>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-green)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {resumenHistorico.habriamosGanadoCount} de {resumenHistorico.totalEvaluadas}
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{resumenHistorico.habriamosGanadoPct}% de efectividad</span>
            </div>

            <div style={{ background: 'var(--bg-hover)', padding: '12px 14px', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>🔴 Pérdidas por Sobreprecio</span>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-red)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {resumenHistorico.perdidasPorSobreprecioCount}
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Sugerencia por sobre el adjudicatario</span>
            </div>

            <div style={{ background: 'var(--bg-hover)', padding: '12px 14px', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>🟡 Subcotizadas (Margen en la mesa)</span>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-yellow)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {resumenHistorico.subcotizadasCount}
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Ganadas pero con descuento excesivo</span>
            </div>

            <div style={{ background: 'var(--bg-hover)', padding: '12px 14px', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>📉 Error Medio MAPE</span>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {resumenHistorico.errorMedioMapePct}%
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Desviación promedio en dinero</span>
            </div>
          </div>
        )}

        {/* Barra de Búsqueda y Filtros al 100% */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Buscador de Texto */}
            <div style={{ flex: '1 1 220px', position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                value={filtroBusqueda}
                onChange={(e) => setFiltroBusqueda(e.target.value)}
                placeholder="Buscar por código, nombre o institución…"
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12.5,
                  color: 'var(--text-primary)',
                }}
              />
              {filtroBusqueda && (
                <button
                  type="button"
                  onClick={() => setFiltroBusqueda('')}
                  style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  <IconoCerrar size={12} />
                </button>
              )}
            </div>

            {/* Filtro por Categoría */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                Línea:
              </span>
              <select
                value={filtroCategoriaId}
                onChange={(e) => setFiltroCategoriaId(e.target.value)}
                style={{
                  padding: '6px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">Todas las Líneas</option>
                {(categoriasQuery.data ?? []).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Resultado */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                Veredicto:
              </span>
              <select
                value={filtroResultado}
                onChange={(e) => setFiltroResultado(e.target.value as any)}
                style={{
                  padding: '6px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}
              >
                <option value="TODOS">Todos los Veredictos</option>
                <option value="HABRIAMOS_GANADO">🏆 Solo Habríamos Ganado</option>
                <option value="SOBREPRECIO">🔴 Solo Sobreprecio</option>
                <option value="SUBCOTIZADO">🟡 Solo Subcotizados</option>
              </select>
            </div>

            {/* Umbral de Observaciones Mínimas */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                Historia:
              </span>
              <select
                value={minimoObs}
                onChange={(e) => setMinimoObs(Number(e.target.value))}
                style={{
                  padding: '6px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}
              >
                <option value={1}>1+ caso previo</option>
                <option value={2}>2+ casos previos</option>
                <option value={3}>3+ casos previos</option>
              </select>
            </div>

            {hayFiltrosHistoricosActivos && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={limpiarFiltrosHistoricos}
                style={{ fontSize: 11.5 }}
              >
                <IconoCerrar size={12} />
                <span>Limpiar filtros</span>
              </button>
            )}
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Mostrando <strong>{casosFiltrados.length}</strong> de {casosHistoricos.length} licitaciones auditadas
            {hayFiltrosHistoricosActivos && ' (filtros aplicados)'}
          </div>
        </div>

        {/* Tabla Detallada */}
        {casosFiltrados.length === 0 ? (
          <Vacio
            titulo={hayFiltrosHistoricosActivos ? 'Sin resultados para estos filtros' : 'Aún no hay suficientes casos de validación'}
            detalle={
              hayFiltrosHistoricosActivos
                ? 'Prueba ajustando la búsqueda, seleccionando otra línea de negocio o limpiando los filtros.'
                : 'Registra licitaciones adjudicadas o perdidas con el monto ganador para alimentar el modelo de auditoría.'
            }
            accion={
              hayFiltrosHistoricosActivos ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={limpiarFiltrosHistoricos}>
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Licitación / Proceso</th>
                  <th>Institución Compradora</th>
                  <th style={{ textAlign: 'right' }}>Presupuesto Oficial</th>
                  <th style={{ textAlign: 'right' }}>Ganador Real</th>
                  <th style={{ textAlign: 'right' }}>Sugerido Motor</th>
                  <th style={{ textAlign: 'right' }}>Diferencia</th>
                  <th style={{ textAlign: 'center' }}>Veredicto</th>
                  <th>Explicación Comercial</th>
                </tr>
              </thead>
              <tbody>
                {casosFiltrados.map((caso: CasoValidacionHistorica) => {
                  const esGanada = caso.resultado === 'HABRIAMOS_GANADO';
                  const esSobreprecio = caso.resultado === 'SOBREPRECIO';

                  return (
                    <tr key={caso.licitacionId}>
                      <td>
                        <strong style={{ color: 'var(--text-primary)', display: 'block' }}>{caso.titulo}</strong>
                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {caso.codigoExterno ?? 'Sin código'} · {caso.fechaAdjudicacion ? caso.fechaAdjudicacion.slice(0, 10) : 'Sin fecha'}
                        </span>
                      </td>

                      <td style={{ fontSize: 12 }}>{caso.organismoNombre}</td>

                      <td className="td-mono">{formatearCLP(caso.presupuestoOficialNeto)}</td>

                      <td className="td-mono" style={{ fontWeight: 700 }}>
                        {formatearCLP(caso.precioGanadorRealNeto)}
                      </td>

                      <td className="td-mono" style={{ color: 'var(--accent-blue)', fontWeight: 700 }}>
                        {formatearCLP(caso.precioSugeridoMotorNeto)}
                      </td>

                      <td
                        className="td-mono"
                        style={{
                          color: caso.diferenciaNeto <= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
                          fontWeight: 600,
                        }}
                      >
                        {caso.diferenciaNeto > 0 ? '+' : ''}
                        {formatearCLP(caso.diferenciaNeto)} ({caso.diferenciaPct}%)
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 8px',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 10.5,
                            fontWeight: 700,
                            background: esGanada
                              ? 'var(--accent-green-soft)'
                              : esSobreprecio
                              ? 'var(--accent-red-soft)'
                              : 'var(--accent-yellow-soft)',
                            color: esGanada
                              ? 'var(--accent-green)'
                              : esSobreprecio
                              ? 'var(--accent-red)'
                              : 'var(--accent-yellow)',
                          }}
                        >
                          {esGanada && <IconoCheck size={12} />}
                          {esSobreprecio && <IconoAlerta size={12} />}
                          {!esGanada && !esSobreprecio && <span>⚠️</span>}
                          {esGanada ? 'Habríamos Ganado' : esSobreprecio ? 'Sobreprecio' : 'Subcotizado'}
                        </span>
                      </td>

                      <td style={{ fontSize: 11.5, color: 'var(--text-secondary)', maxWidth: 280, lineHeight: 1.45 }}>
                        {caso.explicacion}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ══ 4. Calibración Fina de Parámetros (Sliders Manuales) ══ */}
      <Panel
        titulo="4. Calibración Manual Fina de Algoritmos"
        subtitulo="Ajusta pesos de recencia, factores de agresividad y piso mínimo de rentabilidad con sliders ergonómicos."
        acciones={
          <div style={{ display: 'flex', gap: 8 }}>
            {hayCambiosManuales && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setBorradorParametros(null)}>
                Descartar cambios
              </button>
            )}
            {esAdmin && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleGuardarParametrosManuales}
                disabled={!hayCambiosManuales || actualizarParametrosMut.isPending}
              >
                {actualizarParametrosMut.isPending ? 'Guardando…' : 'Guardar Parámetros'}
              </button>
            )}
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        {GRUPOS_CONTROLES.map((grupo) => (
          <div key={grupo} style={{ marginBottom: 22 }}>
            <h4
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginBottom: 14,
                paddingBottom: 6,
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              {grupo}
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {CONTROLES.filter((c) => c.grupo === grupo).map((ctrl) => {
                const valorActual = valoresParametros[ctrl.clave];
                const modificado = parametrosActuales ? valorActual !== parametrosActuales[ctrl.clave] : false;

                return (
                  <div
                    key={ctrl.clave}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 280px',
                      gap: 20,
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <label
                          htmlFor={ctrl.clave}
                          style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                        >
                          {ctrl.etiqueta}
                        </label>
                        {modificado && (
                          <span
                            style={{
                              fontSize: 9.5,
                              fontWeight: 800,
                              color: 'var(--accent-blue)',
                              textTransform: 'uppercase',
                              background: 'var(--accent-blue-soft)',
                              padding: '1px 6px',
                              borderRadius: 4,
                            }}
                          >
                            Modificado
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.45 }}>
                        {ctrl.explicacion}
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        id={ctrl.clave}
                        type="range"
                        min={ctrl.min}
                        max={ctrl.max}
                        step={ctrl.paso}
                        value={valorActual}
                        onChange={(e) =>
                          setBorradorParametros({ ...valoresParametros, [ctrl.clave]: Number(e.target.value) })
                        }
                        style={{ flex: 1, accentColor: modificado ? 'var(--accent-blue)' : 'var(--accent-gray)' }}
                        disabled={!esAdmin}
                      />
                      <span
                        className="td-mono"
                        style={{
                          minWidth: 70,
                          textAlign: 'right',
                          fontSize: 13,
                          fontWeight: 700,
                          color: modificado ? 'var(--accent-blue)' : 'var(--text-primary)',
                        }}
                      >
                        {ctrl.paso < 1 ? valorActual.toFixed(2) : valorActual}
                        {ctrl.sufijo ? ` ${ctrl.sufijo}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </Panel>

      {/* ══ 5. Calidad de Datos & Brechas de Plazo ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Panel titulo="Observaciones por Categoría" subtitulo="De aquí se deduce la confianza estadística del modelo">
          {diagnosticoQuery.data?.porCategoria && diagnosticoQuery.data.porCategoria.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {diagnosticoQuery.data.porCategoria.map((cat) => {
                const maximo = Math.max(...(diagnosticoQuery.data?.porCategoria.map((c) => c.observaciones) ?? [1]), 1);
                const suficiente = cat.observaciones >= 4;

                return (
                  <div key={cat.categoriaId} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{cat.nombre}</span>
                      <strong
                        style={{
                          fontFamily: 'var(--font-mono)',
                          color: suficiente ? 'var(--accent-green)' : cat.observaciones > 0 ? 'var(--accent-yellow)' : 'var(--text-muted)',
                        }}
                      >
                        {cat.observaciones} {cat.observaciones === 1 ? 'proceso' : 'procesos'}
                      </strong>
                    </div>
                    <div className="progress-track" style={{ height: 6 }}>
                      <div
                        className="progress-fill"
                        style={{
                          width: `${(cat.observaciones / maximo) * 100}%`,
                          background: suficiente ? 'var(--accent-green)' : 'var(--accent-yellow)',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Vacio titulo="Sin datos por categoría" />
          )}
        </Panel>

        <Panel titulo="Brecha de Plazos frente al Ganador" subtitulo="Positivo indica que ofertamos más lento que el ganador real">
          {brechasQuery.data && brechasQuery.data.some((b) => b.n > 0) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {brechasQuery.data
                .filter((b) => b.n > 0)
                .map((b) => (
                  <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {b.nombre} <small style={{ color: 'var(--text-muted)' }}>({b.n} procesos)</small>
                    </span>
                    <strong
                      className="td-mono"
                      style={{ color: b.brechaPromedio > 0 ? 'var(--accent-red)' : 'var(--accent-green)', fontSize: 13 }}
                    >
                      {b.brechaPromedio > 0 ? `+${b.brechaPromedio.toFixed(1)} días` : `${b.brechaPromedio.toFixed(1)} días`}
                    </strong>
                  </div>
                ))}
            </div>
          ) : (
            <Vacio
              titulo="Sin registro de brechas de plazo"
              detalle="Al registrar actas de licitaciones perdidas indicando los días de entrega del rival, el sistema mide si perdiste por precio o por plazo."
            />
          )}
        </Panel>
      </div>

      {/* ══ Modal de Test de Impacto Antes vs Después ══ */}
      {modalImpactoAbierto && (
        <div className="modal-overlay modal-backdrop" onClick={() => setModalImpactoAbierto(false)}>
          <div className="modal modal-container" style={{ maxWidth: 660 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconoMotorIA size={18} style={{ color: 'var(--accent-blue)' }} />
                Test de Impacto Comercial (Antes vs Después)
              </h3>
              <button
                type="button"
                className="btn-icono"
                onClick={() => setModalImpactoAbierto(false)}
                aria-label="Cerrar modal"
              >
                <IconoCerrar size={14} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, marginTop: 0, marginBottom: 18 }}>
                Proyección prospectiva evaluando cómo cambiaría la tasa de adjudicaciones, el error medio y el margen si
                aplicas la postura propuesta frente a la configuración vigente.
              </p>

              {/* Selector de Modo para proyectar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 22 }}>
                {[
                  { modo: 'AGRESIVO' as const, etiqueta: 'Modo Agresivo', icono: IconoRayo, acento: 'var(--accent-yellow)', acentoSoft: 'var(--accent-yellow-soft)' },
                  { modo: 'EQUILIBRADO' as const, etiqueta: 'Modo Equilibrado', icono: IconoCerebro, acento: 'var(--accent-green)', acentoSoft: 'var(--accent-green-soft)' },
                  { modo: 'CONSERVADOR' as const, etiqueta: 'Modo Rentabilidad', icono: IconoEscudoCheck, acento: 'var(--accent-blue)', acentoSoft: 'var(--accent-blue-soft)' },
                ].map(({ modo, etiqueta, icono: Icono, acento, acentoSoft }) => {
                  const estaActivo = modoParaImpacto === modo;
                  return (
                    <button
                      key={modo}
                      type="button"
                      onClick={() => handleAbrirTestImpacto(modo)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 12.5,
                        fontWeight: estaActivo ? 700 : 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        border: estaActivo ? `2px solid ${acento}` : '1px solid var(--border-subtle)',
                        background: estaActivo ? acentoSoft : 'var(--bg-hover)',
                        color: estaActivo ? acento : 'var(--text-secondary)',
                        boxShadow: estaActivo ? `0 2px 8px ${acentoSoft}` : 'none',
                      }}
                    >
                      <Icono size={14} style={{ color: acento, flexShrink: 0 }} />
                      <span>{etiqueta}</span>
                    </button>
                  );
                })}
              </div>

              {testImpactoMut.isPending ? (
                <Cargando texto="Calculando proyección histórica de impacto…" />
              ) : resultadoImpacto ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {/* Configuración Actual */}
                    <div
                      style={{
                        background: 'var(--bg-hover)',
                        border: '1px solid var(--border-subtle)',
                        padding: 16,
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          color: 'var(--text-muted)',
                          marginBottom: 10,
                          letterSpacing: '0.03em',
                        }}
                      >
                        Configuración Actual ({diagnostico.estrategiaActiva})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Tasa de Acierto:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            {resultadoImpacto.configuracionActual.tasaAciertoPct}%
                          </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Error Medio:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            ±{resultadoImpacto.configuracionActual.errorMedioPct}%
                          </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Margen Proyectado:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            {resultadoImpacto.configuracionActual.margenPromedioEstimadoPct}%
                          </strong>
                        </div>
                      </div>
                    </div>

                    {/* Configuración Propuesta */}
                    <div
                      style={{
                        background:
                          modoParaImpacto === 'AGRESIVO'
                            ? 'var(--accent-yellow-soft)'
                            : modoParaImpacto === 'EQUILIBRADO'
                            ? 'var(--accent-green-soft)'
                            : 'var(--accent-blue-soft)',
                        border: `1.5px solid ${
                          modoParaImpacto === 'AGRESIVO'
                            ? 'var(--accent-yellow)'
                            : modoParaImpacto === 'EQUILIBRADO'
                            ? 'var(--accent-green)'
                            : 'var(--accent-blue)'
                        }`,
                        padding: 16,
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          color:
                            modoParaImpacto === 'AGRESIVO'
                              ? 'var(--accent-yellow)'
                              : modoParaImpacto === 'EQUILIBRADO'
                              ? 'var(--accent-green)'
                              : 'var(--accent-blue)',
                          marginBottom: 10,
                          letterSpacing: '0.03em',
                        }}
                      >
                        Propuesta ({modoParaImpacto === 'CONSERVADOR' ? 'Modo Rentabilidad' : `Modo ${modoParaImpacto.charAt(0) + modoParaImpacto.slice(1).toLowerCase()}`})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Tasa de Acierto:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-green)', fontWeight: 700 }}>
                            {resultadoImpacto.configuracionPropuesta.tasaAciertoPct}%
                          </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Error Medio:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            ±{resultadoImpacto.configuracionPropuesta.errorMedioPct}%
                          </strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Margen Proyectado:</span>
                          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                            {resultadoImpacto.configuracionPropuesta.margenPromedioEstimadoPct}%
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Conclusión */}
                  <div
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      padding: '14px 16px',
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                    }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>🚀</span>
                    <div>
                      <strong style={{ color: 'var(--text-primary)' }}>Conclusión: </strong>
                      <span style={{ color: 'var(--text-secondary)' }}>{resultadoImpacto.conclusion}</span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModalImpactoAbierto(false)}>
                Cerrar
              </button>
              {esAdmin && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAplicarDesdeModalImpacto}
                  disabled={aplicarEstrategiaMut.isPending}
                >
                  {aplicarEstrategiaMut.isPending
                    ? 'Aplicando…'
                    : `Aplicar ${modoParaImpacto === 'CONSERVADOR' ? 'Modo Rentabilidad' : modoParaImpacto === 'EQUILIBRADO' ? 'Modo Equilibrado' : 'Modo Agresivo'}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
